import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import type { Hunt } from '@wehunt/ui'
import { useApiFetch } from '../../shared/api'
import type { EndedHuntDto } from './useEndedHunts'

/**
 * A live hunt rides the same wire shape as an ended one — it is the same
 * hunts/{id} document, just not ended yet — so the ongoing feed reuses
 * {@link EndedHuntDto}. The 'ongoing' status is implied by the feed it came
 * through, not by a field on the DTO.
 */
export interface OngoingHuntsResponse {
  hunts: EndedHuntDto[]
}

function pad2(n: number): string {
  return n.toString().padStart(2, '0')
}

/**
 * Maps an ongoing-hunt DTO to a {@link Hunt} flagged status 'ongoing', so the
 * list groups it as a live hunt and the detail opens read-only ("Ongoing").
 * A live hunt has no end yet, so `endDate`/`end` fall back to the start.
 */
export function ongoingDtoToHunt(dto: EndedHuntDto): Hunt {
  const start = new Date(dto.startTime)
  const end = dto.endTime ? new Date(dto.endTime) : start
  const quarry = dto.huntableGames.length > 0 ? dto.huntableGames.join(', ') : '—'
  return {
    id: dto.id,
    date: `${start.getFullYear()}-${pad2(start.getMonth() + 1)}-${pad2(start.getDate())}`,
    endDate: dto.endTime
      ? `${end.getFullYear()}-${pad2(end.getMonth() + 1)}-${pad2(end.getDate())}`
      : undefined,
    start: `${pad2(start.getHours())}:${pad2(start.getMinutes())}`,
    end: `${pad2(end.getHours())}:${pad2(end.getMinutes())}`,
    team: dto.groupName,
    teamId: dto.groupId,
    quarry,
    location: dto.huntName,
    participants: dto.participantCount,
    dogs: [],
    status: 'ongoing',
  }
}

/**
 * The signed-in user's live (ongoing/paused) hunts. Without this feed the web
 * had no query returning ongoing hunts, so a hunt in progress never appeared in
 * the list nor opened (CLUB-691).
 */
export function useOngoingHunts() {
  const call = useApiFetch()
  const { state } = useAuth()
  const uid = state.status === 'signed-in' ? state.user.uid : undefined
  return useQuery({
    queryKey: ['clubhouse', 'me', 'hunts', 'ongoing', uid],
    queryFn: () => call<OngoingHuntsResponse>('/clubhouse/me/hunts/ongoing'),
    enabled: !!uid,
    staleTime: 60 * 1000,
  })
}
